import React, { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Card, CardHeader, CardContent } from '../components/Card';
import { Badge } from '../components/Badge';
import { BackgroundSlider } from '../components/BackgroundSlider';
import { ArrowRight, Check, AlertCircle, Loader2 } from 'lucide-react';
interface AIResearchAssessmentPageProps {
  onNavigate?: (path: string) => void;
}
export function AIResearchAssessmentPage({
  onNavigate
}: AIResearchAssessmentPageProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    role: '',
    niche: '',
    postingFrequency: '',
    biggestFrustration: '',
    frustrationLevel: '',
    authorityVision: '',
    monetizationGoals: [] as string[],
    currentBlockers: '',
    commitmentLevel: '',
    impactOf2026: '',
    breakthroughDefinition: ''
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      value,
      checked
    } = e.target;
    if (checked) {
      setFormData(prev => ({
        ...prev,
        monetizationGoals: [...prev.monetizationGoals, value]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        monetizationGoals: prev.monetizationGoals.filter(goal => goal !== value)
      }));
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');
    try {
      const response = await fetch('https://formspree.io/f/xdakqrgq', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setSubmitStatus('success');
        // Redirect to WhatsApp after 2 seconds
        setTimeout(() => {
          window.location.href = 'https://chat.whatsapp.com/KuVilCGv5Zn8KqDmdOUoee';
        }, 2000);
      } else {
        const data = await response.json();
        setSubmitStatus('error');
        setErrorMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (submitStatus === 'success') {
    return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933] font-sans flex flex-col" data-id="element-240">
        <Navbar onNavigate={onNavigate} data-id="element-241" />
        <div className="flex-grow flex items-center justify-center p-4" data-id="element-242">
          <Card className="max-w-md w-full text-center p-8 border-2 border-[#00d9ff]" data-id="element-243">
            <div className="flex justify-center mb-6" data-id="element-244">
              <div className="w-16 h-16 bg-[#00d9ff]/10 rounded-full flex items-center justify-center" data-id="element-245">
                <Check className="w-8 h-8 text-[#00d9ff]" data-id="element-246" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-[#1f2933] mb-4" data-id="element-247">
              Assessment Submitted
            </h2>
            <p className="text-[#1f2933]/70 mb-8 font-mono text-sm" data-id="element-248">
              Redirecting you to the Authority Builders community...
            </p>
            <div className="w-full bg-[#9ca3af]/20 h-1 rounded-full overflow-hidden" data-id="element-249">
              <div className="h-full bg-[#00d9ff] animate-[loading_2s_ease-in-out_forwards]" style={{
              width: '100%'
            }} data-id="element-250"></div>
            </div>
          </Card>
        </div>
        <Footer onNavigate={onNavigate} data-id="element-251" />
      </div>;
  }
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933] font-sans selection:bg-[#00d9ff]/20 selection:text-[#1f2933]" data-id="element-252">
      <Navbar onNavigate={onNavigate} data-id="element-253" />
      <BackgroundSlider data-id="element-254" />

      <div className="relative z-10 py-12 px-4 sm:px-6 lg:px-8" data-id="element-255">
        <div className="max-w-3xl mx-auto" data-id="element-256">
          {/* Header */}
          <div className="text-center mb-12" data-id="element-257">
            <Badge variant="secondary" className="mb-4 mx-auto font-mono text-xs tracking-wider border-[#00d9ff] text-[#00d9ff] bg-[#00d9ff]/10" data-id="element-258">
              AUTHORITY_ASSESSMENT
            </Badge>
            <h1 className="text-3xl md:text-5xl font-bold text-[#1f2933] mb-4" data-id="element-259">
              Authority Builder Assessment
            </h1>
            <p className="text-lg text-[#1f2933]/70 font-mono max-w-2xl mx-auto" data-id="element-260">
              Help us understand your content creation challenges so we can help
              you build unstoppable authority and income in 2026.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8" data-id="element-261">
            {/* Section 1: Professional Profile */}
            <Card className="border-l-4 border-l-[#1f2933]" data-id="element-262">
              <CardHeader title="01_PROFESSIONAL_IDENTITY" className="font-mono text-xs font-bold text-[#7c3aed] border-b border-dashed border-[#9ca3af]/30 pb-2 mb-4" data-id="element-263" />
              <CardContent className="space-y-6" data-id="element-264">
                <div className="grid md:grid-cols-2 gap-6" data-id="element-265">
                  <div className="space-y-2" data-id="element-266">
                    <label htmlFor="fullName" className="block text-sm font-bold text-[#1f2933]" data-id="element-267">
                      Full Name <span className="text-red-500" data-id="element-268">*</span>
                    </label>
                    <input type="text" id="fullName" name="fullName" required value={formData.fullName} onChange={handleInputChange} className="w-full px-4 py-2 bg-white border border-[#9ca3af] focus:border-[#00d9ff] focus:ring-1 focus:ring-[#00d9ff] outline-none transition-colors font-mono text-sm" placeholder="Enter your full name" data-id="element-269" />
                  </div>
                  <div className="space-y-2" data-id="element-270">
                    <label htmlFor="email" className="block text-sm font-bold text-[#1f2933]" data-id="element-271">
                      Email Address <span className="text-red-500" data-id="element-272">*</span>
                    </label>
                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full px-4 py-2 bg-white border border-[#9ca3af] focus:border-[#00d9ff] focus:ring-1 focus:ring-[#00d9ff] outline-none transition-colors font-mono text-sm" placeholder="Enter your email" data-id="element-273" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6" data-id="element-274">
                  <div className="space-y-2" data-id="element-275">
                    <label htmlFor="role" className="block text-sm font-bold text-[#1f2933]" data-id="element-276">
                      Current Role <span className="text-red-500" data-id="element-277">*</span>
                    </label>
                    <select id="role" name="role" required value={formData.role} onChange={handleInputChange} className="w-full px-4 py-2 bg-white border border-[#9ca3af] focus:border-[#00d9ff] focus:ring-1 focus:ring-[#00d9ff] outline-none transition-colors font-mono text-sm" data-id="element-278">
                      <option value="" data-id="element-279">Select your role...</option>
                      <option value="Coach/Consultant" data-id="element-280">Coach/Consultant</option>
                      <option value="Business Owner" data-id="element-281">Business Owner</option>
                      <option value="Corporate Executive" data-id="element-282">
                        Corporate Executive
                      </option>
                      <option value="Entrepreneur" data-id="element-283">Entrepreneur</option>
                      <option value="Freelance Expert" data-id="element-284">Freelance Expert</option>
                      <option value="Thought Leader" data-id="element-285">Thought Leader</option>
                      <option value="Other" data-id="element-286">Other</option>
                    </select>
                  </div>
                  <div className="space-y-2" data-id="element-287">
                    <label htmlFor="niche" className="block text-sm font-bold text-[#1f2933]" data-id="element-288">
                      Your Niche/Industry{' '}
                      <span className="text-red-500" data-id="element-289">*</span>
                    </label>
                    <input type="text" id="niche" name="niche" required value={formData.niche} onChange={handleInputChange} className="w-full px-4 py-2 bg-white border border-[#9ca3af] focus:border-[#00d9ff] focus:ring-1 focus:ring-[#00d9ff] outline-none transition-colors font-mono text-sm" placeholder="e.g., Leadership coaching, SaaS marketing" data-id="element-290" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 2: Consistency Diagnosis */}
            <Card className="border-l-4 border-l-[#00d9ff]" data-id="element-291">
              <CardHeader title="02_CONSISTENCY_DIAGNOSIS" className="font-mono text-xs font-bold text-[#00d9ff] border-b border-dashed border-[#9ca3af]/30 pb-2 mb-4" data-id="element-292" />
              <CardContent className="space-y-8" data-id="element-293">
                <div className="space-y-3" data-id="element-294">
                  <label className="block text-sm font-bold text-[#1f2933]" data-id="element-295">
                    How often do you currently post content?{' '}
                    <span className="text-red-500" data-id="element-296">*</span>
                  </label>
                  <div className="space-y-2" data-id="element-297">
                    {['Rarely (Once a month or less)', 'Inconsistently (Few times a month)', 'Somewhat regularly (1-2 times per week)', 'Consistently (3+ times per week)'].map(freq => <label key={freq} className="flex items-center space-x-3 p-3 border border-dashed border-[#9ca3af] hover:bg-white cursor-pointer transition-colors" data-id="element-298">
                        <input type="radio" name="postingFrequency" value={freq} checked={formData.postingFrequency === freq} onChange={handleInputChange} required className="text-[#00d9ff] focus:ring-[#00d9ff]" data-id="element-299" />
                        <span className="text-sm text-[#1f2933]" data-id="element-300">{freq}</span>
                      </label>)}
                  </div>
                </div>

                <div className="space-y-3" data-id="element-301">
                  <label className="block text-sm font-bold text-[#1f2933]" data-id="element-302">
                    What's your biggest frustration with content creation?{' '}
                    <span className="text-red-500" data-id="element-303">*</span>
                  </label>
                  <div className="space-y-2" data-id="element-304">
                    {['I have so much to say but never find time to create', "I start strong but can't maintain consistency", "I don't know what content will actually build authority", "I create content but it doesn't lead to opportunities/income", "I'm overwhelmed by the content creation process"].map(frustration => <label key={frustration} className="flex items-center space-x-3 p-3 border border-dashed border-[#9ca3af] hover:bg-white cursor-pointer transition-colors" data-id="element-305">
                        <input type="radio" name="biggestFrustration" value={frustration} checked={formData.biggestFrustration === frustration} onChange={handleInputChange} required className="text-[#00d9ff] focus:ring-[#00d9ff]" data-id="element-306" />
                        <span className="text-sm text-[#1f2933]" data-id="element-307">
                          {frustration}
                        </span>
                      </label>)}
                  </div>
                </div>

                <div className="space-y-3" data-id="element-308">
                  <label className="block text-sm font-bold text-[#1f2933]" data-id="element-309">
                    On a scale of 1-10, how frustrated are you with your current
                    content consistency? <span className="text-red-500" data-id="element-310">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-311">
                    {Array.from({
                    length: 10
                  }, (_, i) => i + 1).map(num => <label key={num} className={`flex-1 min-w-[40px] h-10 flex items-center justify-center border cursor-pointer transition-all ${formData.frustrationLevel === String(num) ? 'bg-[#1f2933] text-white border-[#1f2933]' : 'border-[#9ca3af] hover:border-[#00d9ff] bg-white'}`} data-id="element-312">
                        <input type="radio" name="frustrationLevel" value={num} checked={formData.frustrationLevel === String(num)} onChange={handleInputChange} required className="sr-only" data-id="element-313" />
                        <span className="font-mono text-sm" data-id="element-314">{num}</span>
                      </label>)}
                  </div>
                  <div className="flex justify-between text-xs font-mono text-[#9ca3af]" data-id="element-315">
                    <span data-id="element-316">Not frustrated</span>
                    <span data-id="element-317">Extremely frustrated</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 3: Authority & Monetization Goals */}
            <Card className="border-l-4 border-l-[#7c3aed]" data-id="element-318">
              <CardHeader title="03_GOALS_&_ASPIRATIONS" className="font-mono text-xs font-bold text-[#7c3aed] border-b border-dashed border-[#9ca3af]/30 pb-2 mb-4" data-id="element-319" />
              <CardContent className="space-y-6" data-id="element-320">
                <div className="space-y-2" data-id="element-321">
                  <label htmlFor="authorityVision" className="block text-sm font-bold text-[#1f2933]" data-id="element-322">
                    What does authority in your niche look like to you?{' '}
                    <span className="text-red-500" data-id="element-323">*</span>
                  </label>
                  <textarea id="authorityVision" name="authorityVision" required value={formData.authorityVision} onChange={handleInputChange} rows={3} className="w-full px-4 py-2 bg-white border border-[#9ca3af] focus:border-[#7c3aed] focus:ring-1 focus:ring-[#7c3aed] outline-none transition-colors font-mono text-sm resize-none" placeholder="Describe your vision: speaking gigs, book deals, high-ticket clients, industry recognition..." data-id="element-324" />
                </div>

                <div className="space-y-3" data-id="element-325">
                  <label className="block text-sm font-bold text-[#1f2933]" data-id="element-326">
                    How do you want to monetize your authority? (Optional)
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-327">
                    {['High-ticket coaching/consulting', 'Digital products/courses', 'Speaking engagements', 'Sponsorships/partnerships', 'Book deals', 'Corporate training', 'Other'].map(goal => <label key={goal} className="flex items-center space-x-2" data-id="element-328">
                        <input type="checkbox" value={goal} checked={formData.monetizationGoals.includes(goal)} onChange={handleCheckboxChange} className="rounded text-[#00d9ff] focus:ring-[#00d9ff]" data-id="element-329" />
                        <span className="text-sm text-[#1f2933]" data-id="element-330">{goal}</span>
                      </label>)}
                  </div>
                </div>

                <div className="space-y-2" data-id="element-331">
                  <label htmlFor="currentBlockers" className="block text-sm font-bold text-[#1f2933]" data-id="element-332">
                    What's currently stopping you from building the authority
                    you deserve? <span className="text-red-500" data-id="element-333">*</span>
                  </label>
                  <textarea id="currentBlockers" name="currentBlockers" required value={formData.currentBlockers} onChange={handleInputChange} rows={4} className="w-full px-4 py-2 bg-white border border-[#9ca3af] focus:border-[#7c3aed] focus:ring-1 focus:ring-[#7c3aed] outline-none transition-colors font-mono text-sm resize-none" placeholder="Be honest about the real blockers: time, clarity, confidence, systems, accountability..." data-id="element-334" />
                </div>
              </CardContent>
            </Card>

            {/* Section 4: 2026 Commitment */}
            <Card className="border-l-4 border-l-[#1f2933]" data-id="element-335">
              <CardHeader title="04_2026_COMMITMENT" className="font-mono text-xs font-bold text-[#1f2933] border-b border-dashed border-[#9ca3af]/30 pb-2 mb-4" data-id="element-336" />
              <CardContent className="space-y-6" data-id="element-337">
                <div className="space-y-3" data-id="element-338">
                  <label className="block text-sm font-bold text-[#1f2933]" data-id="element-339">
                    Are you ready to commit to consistent content creation
                    throughout 2026? <span className="text-red-500" data-id="element-340">*</span>
                  </label>
                  <div className="space-y-2" data-id="element-341">
                    {["Yes, I'm all in for 2026", 'I want to, but I need the right system', "I'm not sure yet"].map(option => <label key={option} className="flex items-center space-x-3 p-3 border border-dashed border-[#9ca3af] hover:bg-white cursor-pointer transition-colors" data-id="element-342">
                        <input type="radio" name="commitmentLevel" value={option} checked={formData.commitmentLevel === option} onChange={handleInputChange} required className="text-[#1f2933] focus:ring-[#1f2933]" data-id="element-343" />
                        <span className="text-sm text-[#1f2933]" data-id="element-344">{option}</span>
                      </label>)}
                  </div>
                </div>

                <div className="space-y-2" data-id="element-345">
                  <label htmlFor="impactOf2026" className="block text-sm font-bold text-[#1f2933]" data-id="element-346">
                    If you could stay consistent for all of 2026, what would
                    change in your business/career?{' '}
                    <span className="text-red-500" data-id="element-347">*</span>
                  </label>
                  <textarea id="impactOf2026" name="impactOf2026" required value={formData.impactOf2026} onChange={handleInputChange} rows={4} className="w-full px-4 py-2 bg-white border border-[#9ca3af] focus:border-[#1f2933] focus:ring-1 focus:ring-[#1f2933] outline-none transition-colors font-mono text-sm resize-none" placeholder="Paint the picture: more clients, higher fees, industry recognition, passive income..." data-id="element-348" />
                </div>

                <div className="space-y-2" data-id="element-349">
                  <label htmlFor="breakthroughDefinition" className="block text-sm font-bold text-[#1f2933]" data-id="element-350">
                    What would make 2026 your breakthrough year?{' '}
                    <span className="text-red-500" data-id="element-351">*</span>
                  </label>
                  <textarea id="breakthroughDefinition" name="breakthroughDefinition" required value={formData.breakthroughDefinition} onChange={handleInputChange} rows={3} className="w-full px-4 py-2 bg-white border border-[#9ca3af] focus:border-[#1f2933] focus:ring-1 focus:ring-[#1f2933] outline-none transition-colors font-mono text-sm resize-none" placeholder="What specific outcome would make you say 'This was the year everything changed'?" data-id="element-352" />
                </div>
              </CardContent>
            </Card>

            {/* Error Message */}
            {submitStatus === 'error' && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2" data-id="element-353">
                <AlertCircle className="w-5 h-5" data-id="element-354" />
                <p className="text-sm" data-id="element-355">{errorMessage}</p>
              </div>}

            {/* Submit Button */}
            <div className="flex justify-center pt-4" data-id="element-356">
              <Button type="submit" size="large" disabled={isSubmitting} className="w-full sm:w-auto min-w-[250px] bg-[#1f2933] hover:bg-[#00d9ff] hover:text-[#1f2933] text-white border-[#1f2933] hover:border-[#00d9ff] transition-all duration-300" rightIcon={isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" data-id="element-358" /> : <ArrowRight className="w-4 h-4" data-id="element-359" />} data-id="element-357">
                {isSubmitting ? 'PROCESSING...' : 'SUBMIT_ASSESSMENT'}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <Footer onNavigate={onNavigate} data-id="element-360" />
    </div>;
}